import * as turf from '@turf/turf';
import {PredictService} from '../../../../services/predict.service';
import {distanceVisibleInCamera, getCameraBoundingBox, subdivideBbox, toBBoxString} from '../../cesiumTools';
import { LayerDatasource, LayerLoadType, LayerType, MapperViewer} from '../../types';

export class ZmffLayer implements LayerDatasource {
  id = 'zmff';
  name = 'ZMF';
  active = false;
  dataSource = new Cesium.GeoJsonDataSource(this.name);
  type = LayerType.DataSource;
  loadType = LayerLoadType.Manual;
  viewer: MapperViewer;

  /* Custom properties */
  zmffColor = Cesium.Color.fromCssColorString('#88ff80').withAlpha(0.6);
  zmffColorMoyen = Cesium.Color.fromCssColorString('#55c344').withAlpha(0.6);
  zmffColorFort = Cesium.Color.fromCssColorString('#126000').withAlpha(0.6);
  detailVisible = true;
  previouslyLoadedZmff = null;
  isLoading = false;

  hasDetail = true;

  constructor(
    private predictService: PredictService,
  ) {}

  remove(): void {
    this.viewer.dataSources.remove(this.dataSource);
  }

  initialize(viewer: MapperViewer): void {
    this.viewer = viewer;
    viewer.dataSources.add(this.dataSource);
  }

  setDetailVisible(visible: boolean): void {
    this.detailVisible = visible;
    this.applyStyle();
  }

  loadData(forceReload = false): void {
    if (!this.dataSource) {
      console.error('Viewer not initialized');
      return;
    }

    const distance = distanceVisibleInCamera(this.viewer);

    if (!distance || distance > 41) {
      return;
    }

    const bbox = toBBoxString(getCameraBoundingBox(this.viewer));

    if (this.previouslyLoadedZmff === bbox && !forceReload) {
      return;
    }

    this.previouslyLoadedZmff = bbox;

    this.isLoading = true;

    this.fetchZmff(bbox).then((features) => {
      this.buildZmff(this.dedupeFeatures(features));
      this.isLoading = false;
    });
  }

  private dedupeFeatures(features: any[]): any[] {
    const featureById = new Map<string, any>();

    features.forEach((feature) => {
      const id = feature.properties.id;
      const existing = featureById.get(id);

      if (!existing || turf.area(feature) > turf.area(existing)) {
        featureById.set(id, feature);
      }
    });

    return Array.from(featureById.values());
  }

  private fetchZmff(bbox: string): Promise<any[]> {
    return new Promise((resolve) => {
      this.predictService.getZmff(bbox).subscribe((geojson) => {
        resolve(geojson.features);
      }, () => {
        const [west, north, east, south] = bbox.split(',').map(Number);
        const subBboxes = subdivideBbox([west, south, east, north]);

        if (subBboxes.length === 0) {
          resolve([]);

          return;
        }

        Promise.all(
          subBboxes.map(([subWest, subSouth, subEast, subNorth]) =>
            this.fetchZmff(`${subWest},${subNorth},${subEast},${subSouth}`))
        ).then((results) => resolve(results.flat()));
      });
    });
  }

  buildZmff(features): void {
    if (!features?.length) {
      return;
    }

    try {
      this.dataSource.load({
        type: 'FeatureCollection',
        features
      }, {
        stroke: this.zmffColor,
        fill: this.zmffColor,
        strokeWidth: 3,
        clampToGround: true,
      }).then(() => {
        this.applyStyle();
        this.setActive(true);
      });
    } catch (error){
      console.log(error);
    }
  }

  applyStyle(): void {
    this.dataSource.entities.values.forEach((item) => {

      if (this.detailVisible) {
        switch (item.properties.niveau.getValue()) {
          case 'FORT':
            item.polygon.material = this.zmffColorFort;
            break;
          case 'MOYEN':
            item.polygon.material = this.zmffColorMoyen;
            break;
          default:
            item.polygon.material = this.zmffColor;
            break;
        }
      } else {
        item.polygon.material = this.zmffColor;
      }
    });

    this.viewer.render();
  }

  setActive(active: boolean): void  {
    this.active = active;
    this.dataSource.show = active;
  }

  onAfterPrint(): void {
  }

  onBeforePrint(): void {
  }
}
